import { useGetStaffListQuery } from "@/services/StaffsApi";
import { LoadingButton } from "@/components/ui/loading-button/LoadingButton";
import { Separator } from "@/components/ui/Separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import { toast } from "@/components/ui/UseToast";
import { useEffect, useState } from "react";
import { FloatingLabelInput } from "@/components/ui/floating-lable/Floating";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { Status } from "@/app/shared_enums/enums";
import { Button } from "@/components/ui/Button";
import { ChevronDownIcon, Webcam, Check } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { cn } from "@/lib/utils";
import { PhoneNumberUtil } from "google-libphonenumber";
import PhoneInputField from "@/components/custom-component/PhoneInputField";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/Command";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import { ErrorType } from "@/app/types";
import { Department, ReRouteType } from "@/app/types/department";
import {
  useCreateDepartmentMutation,
  useDeleteImageMutation,
  useUpdateDepartmentMutation,
  useUploadImageMutation,
} from "@/services/DepartmentApi";
import { deleteCognitoImage, UploadCognitoImage } from "@/utils/lib/S3Service";
import { default as profileimg } from "@/assets/default.jpg";
import { Switch } from "@/components/ui/Switch";
import { ConfirmationDialog } from "@/components/ui/common/ConfirmationDailog";
import { deepCompareObjects } from "@/utils/Helper";
const { VITE_VIEW_S3_URL, VITE_S3_DELETE_URL, IS_PRODUCTION, VITE_S3_URL } =
  import.meta.env;

const status = [
  { value: "active", label: "Active", color: "bg-green-500" },
  { value: "inactive", label: "Inactive", color: "bg-red-500" },
];

const options = [
  { label: "Within Department", value: ReRouteType.WITHIN_DEPT },
  { label: "Across Department", value: ReRouteType.ACROSS_DEPT },
];
interface DepartmentFormProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  action: "add" | "edit";
  setAction: React.Dispatch<React.SetStateAction<"add" | "edit">>;
  refetch: () => void;
  setData: React.Dispatch<React.SetStateAction<Department | undefined>>;
  data: Department | undefined;
}

const DepartmentForm = ({
  isOpen,
  setOpen,
  action,
  setAction,
  refetch,
  data,
  setData,
}: DepartmentFormProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [uploadImage] = useUploadImageMutation();
  const [deleteImage] = useDeleteImageMutation(); // Add the delete image mutation

  const closeDialog = () => setIsDialogOpen(false);

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.org_id) ?? 0;
  
  const [avatar, setAvatar] = useState<string | ArrayBuffer | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/png", "image/jpg", "image/jpeg"];
    const maxSizeMB = 1;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (file) {
      if (!validTypes.includes(file.type)) {
        toast({
          variant: "destructive",
          title: "Error Uploading Image",
          description:
            "Unsupported image type. Only PNG, JPG, and JPEG are supported.",
        });
        // Reset file input after error
        event.target.value = "";
        return;
      }

      if (file.size > maxSizeBytes) {
        toast({
          variant: "destructive",
          title: "Error Uploading Image",
          description: `Image size exceeds ${maxSizeMB} MB limit.`,
        });
        // Reset file input after error
        event.target.value = "";
        return;
      }

      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target?.result as string;
        img.onload = () => {
          setSelectedImage(file);
          if (e.target?.result !== undefined) {
            setAvatar(e.target.result);
          }
        };
      };

      reader.readAsDataURL(file);
    }
  };

  const handleSetAvatarClick = () => {
    document.getElementById("fileInput")?.click();
  };

  const initialValues = {
    name: "",
    org_id: orgId,
    logo: "",
    status: "active" as Status,
    ticket_rerouting: ReRouteType.WITHIN_DEPT,
    description: "",
    contact_person: "",
    contact_number: "",
    code: "",
  };

  const { data: staffOptions, refetch: staffAddRefetch } = useGetStaffListQuery(
    { query: "" }
  );

  const [createDepartment] = useCreateDepartmentMutation();
  const [updateDepartment] = useUpdateDepartmentMutation();

  const form = useForm<Department>({
    mode: "all",
    defaultValues: initialValues,
  });

  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    formState: { isSubmitting, errors },
  } = form;
  const watcher = watch();

  const openDialog = () => {
    const { flag, differences } = deepCompareObjects(watcher, initialValues);
    if (!flag) {
      setIsDialogOpen(true);
    } else {
      handleClose();
    }
  };

  const phoneUtil = PhoneNumberUtil.getInstance();

  const validatePhone = (value: string | undefined | null) => {
    value = "+" + value;
    if (!value) {
      return true; // If value is empty, validation passes (based on requirements)
    }

    if (value.length > 20) {
      return "Phone number cannot exceed 20 digits";
    }

    if (value.length <= 4) {
      return true; // Pass validation if length is 5 or fewer
    }

    try {
      // Parse the phone number with the default country code (can be adjusted)
      const parsedNumber = phoneUtil.parseAndKeepRawInput(value);

      // Check if the parsed number is a valid phone number
      if (!phoneUtil.isValidNumber(parsedNumber)) {
        return "Invalid phone number";
      }

      return true; // Return true if validation passes
    } catch (error) {
      // Catch parsing errors and return an appropriate message
      return "Invalid phone number format";
    }
  };

  useEffect(() => {
    if (action === "edit" && data) {
      const payload = { ...data };
      reset(payload);
      if(data.logo){
        setAvatar(VITE_VIEW_S3_URL + data.logo);
      }
    } else if (action === "add") {
      reset(initialValues);
    }
  }, [action, data, reset]);

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };
  const formatContactNumber = (contactNumber?: string | null) => {
    return contactNumber ?? `+92`;
  };

  const onSubmit = async (payload: Department) => {
    try {
        // Handle image logic
        if (selectedImage) {
          if (action === "edit" && data?.logo) {
            try {
              await deleteCognitoImage(data?.logo);
            } catch (deleteError) {
              console.error("Failed to delete previous image:", deleteError);
            }
          }
          try {
            const getUrl = await UploadCognitoImage(selectedImage);
            payload.logo = getUrl.location as string;
          } catch (uploadError) {
            payload.logo = "";
            console.error("Failed to upload new image:", uploadError);
          }
        } else if (action === "edit" && data?.logo && avatar !== null) {
          // Only keep the existing logo if it wasn't removed
          payload.logo = avatar === null && !selectedImage ? "" : data.logo;
        } else {
          // If image was removed or no image was selected
          payload.logo = "";
        }
      // Rest of the submission logic
      payload.contact_number = formatContactNumber(payload.contact_number);
      payload.name = payload.name.trim().replace(/\s+/g, " ");
      payload.code = payload.code;
      if (action === "add") {
        await createDepartment(payload)
          .unwrap()
          .then((res) => {
            toast({
              variant: "success",
              title: "Department created successfully",
            });
            refetch();
            handleClose();
            staffAddRefetch();
          })
          .catch((err) => {
            toast({
              variant: "destructive",
              title: `${err?.data?.message}`,
            });
          });
      } else if (action === "edit") {
        await updateDepartment(payload)
          .unwrap()
          .then((res) => {
            toast({
              variant: "success",
              title: "Department updated successfully",
            });
            reset(initialValues);
            refetch();
            staffAddRefetch();
            handleClose();
          })
          .catch((err) => {
            toast({
              variant: "destructive",
              title: `${err?.data?.message}`,
            });
          });
      }
    } catch (error: unknown) {
      console.error("Error", { error });
      const errorMessage =
        error && typeof error === "object" && "data" in error
          ? ((error as ErrorType).data?.detail ??
            (error as ErrorType).data?.message)
          : "Something Went Wrong.";
      toast({
        variant: "destructive",
        title: "Error in form Submission",
        description: errorMessage,
      });
    }
  };

  const handleClose = () => {
    clearErrors();
    reset();
    setAvatar(null);
    setSelectedImage(null);
    setAction("add");
    setData(undefined);
    setOpen(false);
  };
  const handleConfirm = async () => {
    closeDialog();
    handleClose();
  };

  const handleRemoveImageClick = () => {
    setSelectedImage(null);
    setAvatar(null);
    setValue("logo", "");
    const fileInput = document.getElementById("fileInput") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  return (
    <Sheet open={isOpen}>
      <SheetContent
        hideCloseButton
        className="!max-w-[1050px] p-0 custom-scrollbar  sm:w-[90%] sm:max-w-2xl "
      >
        <FormProvider {...form}>
          <form
            onSubmit={handleSubmit(onSubmit, onError)}
            className="flex flex-col  gap-4 bg-gray-100 h-screen"
            noValidate
          >
            <SheetHeader className="sticky top-0 z-40 px-4 py-3 bg-white border-2 border-transparent border-b-gray-200">
              <SheetTitle>
                <div className="flex justify-between gap-5 items-center  bg-white">
                  <p className="font-semibold">
                    {action === "add" ? "Create" : "Edit"} Department
                  </p>

                  <div className="flex gap-2">
                    <Button
                      type={"button"}
                      onClick={openDialog}
                      // onClick={handleClose}
                      className="w-[100px] gap-2  bg-transparent border border-primary text-black hover:border-primary hover:bg-muted"
                    >
                      <i className="fa-regular fa-x  text-xs font-semibold "></i>
                      {""}
                      Cancel
                    </Button>
                    <LoadingButton
                      type="submit"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      className="gap-0 w-[120px]"
                    >
                      {!isSubmitting && (
                        <i className="fa-regular fa-floppy-disk text-base "></i>
                      )}
                      Save
                    </LoadingButton>
                  </div>
                </div>
              </SheetTitle>
            </SheetHeader>
            <SheetDescription className="p-4 mx-4 bg-white rounded-lg">
              <h2 className="font-medium">Department Details </h2>
              <Separator className=" h-[1px] rounded-full my-2" />
              <div className="flex justify-start items-center ">
                <div className="flex flex-row gap-4 items-center just">
                  <div className="flex justify-center items-center ">
                    <img
                      id="avatar"
                      src={
                        avatar
                          ? String(avatar)
                          : watcher.logo
                            ? VITE_VIEW_S3_URL + watcher.logo
                            : profileimg
                      }
                      loading="lazy"
                      alt={profileimg}
                      className="w-24 h-24 rounded-full object-cover relative border-2 border-primary"
                    />
                  </div>
                  <input
                    type="file"
                    id="fileInput"
                    className="hidden"
                    onChange={handleImageChange}
                  />
                  <Button
                    onClick={handleSetAvatarClick}
                    variant={"outline"}
                    type="button"
                    className="px-4 w-60 text-nowrap py-2 bg-transparent gap-2 text-black rounded hover:bg-primary hover:text-gray-500 "
                  >
                    <Webcam className="h-4 w-4" />
                    Select Logo
                  </Button>

                  {(watcher.logo || selectedImage !== null) && (
                    <Button
                      onClick={handleRemoveImageClick}
                      variant={"outline"}
                      type="button"
                      className="px-4 w-fit bg-red-500 text-nowrap py-2 bg-transparent gap-2 text-black rounded hover:bg-primary hover:text-gray-500 "
                    >
                      <i className="fa-regular fa-trash-alt text-red-600"></i>
                    </Button>
                  )}
                </div>
              </div>
              <div className="w-full flex p-4 flex-row gap-4 items-start">
                {/* Department Code Input */}
                <div className="w-[30%] flex flex-col mt-5 gap-1">
                  <label htmlFor="code" className="text-sm font-medium">
                    Department Code: <span className="text-red-500">*</span>
                  </label>
                  <FloatingLabelInput
                    id="code"
                    placeholder="Add a Unique Code"
                    maxLength={40}
                    className="w-full"
                    {...register("code", {
                      required: "Required",
                      maxLength: {
                        value: 40,
                        message: "Maximum 40 characters allowed",
                      },
                    })}
                    error={errors.code?.message}
                  />
                  
                </div>

                {/* Ticket Re-Routing Dropdown */}
                <div className="w-[33%] flex flex-col mt-5 gap-1 ml-5">
                  <label
                    htmlFor="ticket_rerouting"
                    className="text-sm font-medium"
                  >
                    Re-Routing:
                  </label>
                  <Controller
                    name="ticket_rerouting"
                    control={control}
                    render={({ field: { onChange, value } }) => {
                      return (
                        <div className="flex flex-col gap-1">
                          <div className="flex rounded-md overflow-hidden border border-gray-200">
                            <button
                              type="button"
                              onClick={() => onChange(ReRouteType.WITHIN_DEPT)}
                              className={`flex-1 py-1 px-3 text-sm  min-h-[40px] text-nowrap ${
                                value === ReRouteType.WITHIN_DEPT
                                  ? "bg-primary text-white"
                                  : "bg-white"
                              }`}
                            >
                              Within Department
                            </button>
                            <button
                              type="button"
                              onClick={() => onChange(ReRouteType.ACROSS_DEPT)}
                              className={`flex-1 py-1 px-3 text-sm  min-h-[40px] text-nowrap w-full ${
                                value === ReRouteType.ACROSS_DEPT
                                  ? "bg-primary text-white"
                                  : "bg-white"
                              }`}
                            >
                              Across Department
                            </button>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <div className="h-[20px] text-red-500 text-xs mt-[5px]">
                    {errors.ticket_rerouting?.message || ""}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-6 p-4 items-center">
                <div className="flex flex-col h-full space-y-4 row-span-2 min-h-40 ">
                  <div className="h-[50%]">
                    <label htmlFor="name" className="text-sm font-medium">
                      Department Name: <span className="text-red-500">*</span>
                    </label>
                    <FloatingLabelInput
                      id="name"
                      placeholder="Enter Department Name"
                      className="capitalize w-full"
                      onKeyDown={(event) => {
                        if (
                          event.key === " " &&
                          !(event.target as HTMLInputElement).value
                        ) {
                          event.preventDefault();
                        }
                      }}
                      {...register("name", {
                        required: "Required",
                        maxLength: {
                          value: 50,
                          message: "Should be 50 characters or less",
                        },
                        pattern: {
                          value: /^[A-Za-z0-9\-\s]+$/,
                          message:
                            "Only alphabets,numbers, hyphen (-), and spaces are allowed",
                        },
                      })}
                      // error={errors.name?.message}
                    />
                    <div className="h-[20px] text-red-500 text-xs mt-[5px]">
                      {errors.name?.message || ""}
                    </div>
                  </div>

                  {/* Contact Person */}
                  <div className="h-[50%]">
                    <label
                      htmlFor="contact_person"
                      className="text-sm font-medium"
                    >
                      Contact Person 
                      
                      {/* <span className="text-red-500">*</span>: */}
                    </label>
                    <Controller
                      name={"contact_person"}
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <>
                          <div className="relative w-full">
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button className="capitalize w-full scale-100 shadow-sm border-checkboxborder border-[1px] bg-white text-gray-600 hover:bg-white">
                                  <span className="w-full text-left font-normal">
                                    {value
                                      ? staffOptions?.find(
                                          (role) => role.value === value
                                        )?.label
                                      : "Select Contact Person"}
                                  </span>
                                  <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                className="p-0 relative"
                                side="bottom"
                              >
                                <Command className="w-full">
                                  <CommandList>
                                    <CommandInput placeholder="Select Role" />
                                    <CommandEmpty>No Staff found.</CommandEmpty>
                                    <CommandGroup>
                                      {staffOptions?.map((role: any) => (
                                        <CommandItem
                                          value={role.label}
                                          key={role.value}
                                          className="capitalize"
                                          onSelect={() => {
                                            setValue(
                                              "contact_person",
                                              role.value
                                            );
                                            if (role.contact_number) {
                                              // Remove the '+' prefix if present
                                              const formattedNumber =
                                                role.contact_number.startsWith(
                                                  "+"
                                                )
                                                  ? role.contact_number.substring(
                                                      1
                                                    )
                                                  : role.contact_number;
                                              setValue(
                                                "contact_number",
                                                formattedNumber
                                              );
                                            }
                                          }}
                                        >
                                          <Check
                                            className={cn(
                                              "mr-2 h-4 w-4 rounded-full border-2 border-green-500",
                                              role.value == value
                                                ? "opacity-100"
                                                : "opacity-0"
                                            )}
                                          />
                                          {role.label}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </div>
                          <div className="h-[20px] text-red-500 text-xs mt-[5px]">
                            {errors.contact_person?.message || ""}
                          </div>
                        </>
                      )}
                    />
                  </div>
                </div>

                <div className="flex flex-col h-full space-y-4 row-span-2 min-h-40 ">
                  {/* Status */}
                  <div className="h-[50%] flex flex-col gap-2">
                    <label className="text-sm font-medium">Status:</label>
                    <Controller
                      name="status"
                      rules={{ required: "Required" }}
                      control={control}
                      render={({ field: { onChange, value } }) => {
                        const isActive = value === "active";
                        return (
                          <div className="flex items-start">
                            <Switch
                              checked={isActive}
                              onCheckedChange={(checked) =>
                                onChange(checked ? "active" : "inactive")
                              }
                            />
                            <span className="ml-2 text-sm">
                              {isActive ? "Active" : "Inactive"}
                            </span>
                          </div>
                        );
                      }}
                    />
                  </div>
                  {/* contact number */}
                  <div className="h-[50%]">
                    <label
                      htmlFor="contact_number"
                      className="text-sm font-medium"
                    >
                      Contact Number 
                      {/* <span className="text-red-500">*</span>: */}
                    </label>
                    <Controller
                      name={"contact_number"}
                      rules={{
                        validate: validatePhone,
                      }}
                      control={control}
                      render={({
                        field: { onChange, value, onBlur },
                        fieldState: { error },
                      }) => (
                        <>
                          <PhoneInputField
                            value={value as string}
                            onChange={onChange}
                            // error={errors.contact_number?.message as string}
                            placeholder={"Enter phone number"}
                          />
                          <div className="h-[20px] text-red-500 text-xs mt-[5px]">
                            {errors.contact_number?.message || ""}
                          </div>
                        </>
                      )}
                    />
                  </div>
                </div>

                {/* <div className="flex flex-col row-span-2 min-h-40"> */}
                <div className="flex flex-col h-full space-y-1 row-span-2 min-h-40 ">
                  <label htmlFor="description" className="text-sm font-medium">
                    Description:
                  </label>
                  <FloatingLabelInput
                    id="description"
                    placeholder="Enter Description"
                    type="textarea"
                    rows={6}
                    maxLength={255}
                    {...register("description", {
                      maxLength: {
                        value: 255,
                        message: "Should be 255 characters or less",
                      },
                    })}
                    error={errors.description?.message}
                  />
                </div>
              </div>
            </SheetDescription>

            <ConfirmationDialog
              isOpen={isDialogOpen}
              onClose={closeDialog}
              onConfirm={handleConfirm}
            />
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default DepartmentForm;
